import { Injectable } from '@angular/core';
import { ProjectRole, ADMIN_PROJECT_ROLES } from '../constants/roles.constant';

/**
 * Field permission configuration interface
 */
export interface FieldPermission {
  field: string;
  disabledForRoles: ProjectRole[];
}

/**
 * Permission Service
 *
 * Manages role-based access control for the application
 * Uses project-specific roles from lkp_project_role table
 * Uses simple properties to avoid change detection issues with virtual scroll tables
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  /**
   * Simple property for current project role
   * Set once at page load, does not trigger change detection
   */
  private _currentProjectRole: number = 0;

  /**
   * Role check getters - simple property access, no signals
   */
  get hasFullAccess(): boolean {
    return ADMIN_PROJECT_ROLES.includes(this._currentProjectRole);
  }

  get isIntegrator(): boolean {
    return this._currentProjectRole === ProjectRole.INTEGRATOR;
  }

  get isCustomer(): boolean {
    return this._currentProjectRole === ProjectRole.CUSTOMER;
  }

  get isReadOnly(): boolean {
    return this._currentProjectRole === ProjectRole.READONLY;
  }

  get isGridReadOnly(): boolean {
    return this._currentProjectRole === ProjectRole.READONLY   // roleId 3
        || this._currentProjectRole === ProjectRole.CUSTOMER;  // roleId 5
  }

  get isAdmin(): boolean {
    return this._currentProjectRole === ProjectRole.ADMIN;
  }

  get isUser(): boolean {
    return this._currentProjectRole === ProjectRole.USER;
  }

  /**
   * Field-level permissions configuration
   * Centralized mapping of fields to project roles that cannot edit them
   */
  private readonly fieldPermissions: FieldPermission[] = [
    // Technical/Equipment fields - Integrator cannot edit
    { field: 'integratorSkidSerialNo', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'customerSkidNumber', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv1SerialNo', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv2SerialNo', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv3SerialNo', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv4SerialNo', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv5SerialNo', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv6SerialNo', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv7SerialNo', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv1Igbt', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv2Igbt', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv3Igbt', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv4Igbt', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv5Igbt', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv6Igbt', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'inv7Igbt', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'scu', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'dis', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'epanb', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'dcFuses', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'mvXfmrDesign', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'mvXfmrSerialNo', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'lvXfmrSerialNo', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'bushingSize', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'scadaSerialNo', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'trackerSerialNo1', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'trackerSerialNo2', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'wildlifeDeterrentCheck', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'notes', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },
    { field: 'skidStatus', disabledForRoles: [ProjectRole.INTEGRATOR, ProjectRole.READONLY] },

    // Logistics/Shipping fields - Customer cannot edit
    { field: 'billoflading', disabledForRoles: [ProjectRole.CUSTOMER, ProjectRole.READONLY] },
    { field: 'appointmentTime', disabledForRoles: [ProjectRole.CUSTOMER, ProjectRole.READONLY] },
    { field: 'overWeight', disabledForRoles: [ProjectRole.CUSTOMER, ProjectRole.READONLY] },
    { field: 'overDimension', disabledForRoles: [ProjectRole.CUSTOMER, ProjectRole.READONLY] },
    { field: 'logisticsComment', disabledForRoles: [ProjectRole.CUSTOMER, ProjectRole.READONLY] },
    { field: 'deliveryDate', disabledForRoles: [ProjectRole.CUSTOMER, ProjectRole.READONLY] },
    { field: 'shippingStatus', disabledForRoles: [ProjectRole.CUSTOMER, ProjectRole.READONLY] },
    { field: 'estimatedDeliveryDate', disabledForRoles: [ProjectRole.CUSTOMER, ProjectRole.READONLY] },
  ];

  /**
   * Cache for field permission checks
   * Prevents recalculation of the same permission multiple times
   */
  private readonly fieldPermissionCache = new Map<string, boolean>();

  constructor() {}

  /**
   * Set the current project role
   * Called by component after loading user's project permissions
   * Simple property assignment - does not trigger change detection
   *
   * @param roleId - Project role ID from lkp_project_role table
   */
  setProjectRole(roleId: number): void {
    if (this._currentProjectRole !== roleId) {
      this._currentProjectRole = roleId;
      this.clearCache();
    }
  }

  /**
   * Check if a specific field is disabled for current project role
   *
   * @param fieldName - Name of the field to check
   * @param additionalCondition - Optional additional condition to disable field
   * @returns true if field should be disabled
   */
  isFieldDisabled(fieldName: string, additionalCondition: boolean = false): boolean {
    // Early return if additional condition is true
    if (additionalCondition) {
      return true;
    }

    const currentRole = this._currentProjectRole;
    const cacheKey = `${fieldName}_${currentRole}`;

    // Check cache first for performance
    if (this.fieldPermissionCache.has(cacheKey)) {
      return this.fieldPermissionCache.get(cacheKey)!;
    }

    // Find permission configuration for this field
    const permission = this.fieldPermissions.find(p => p.field === fieldName);
    const isDisabled = permission
      ? permission.disabledForRoles.includes(currentRole)
      : false;

    // Cache the result
    this.fieldPermissionCache.set(cacheKey, isDisabled);

    return isDisabled;
  }

  /**
   * Special logic for Skid Configuration field
   * Admin/User: Disabled when status is Complete or In-Progress
   * Others: Always disabled
   *
   * @param skidStatus - Current skid status
   * @returns true if skid configuration should be disabled
   */
  isSkidConfigDisabled(skidStatus: string | null | undefined): boolean {
    const role = this._currentProjectRole;

    // Admin only: Disable when Complete or In-Progress
    if (role === ProjectRole.ADMIN) {
      return skidStatus === 'Complete' || skidStatus === 'In-Progress';
    }

    // User/ReadOnly/Integrator/Customer: Always disabled
    return true;
  }

  /**
   * Special logic for Inverter fields (based on skid configuration)
   * Integrator/ReadOnly: Always disabled
   * Others: Enabled based on N-configuration (e.g., N3 = 3 inverters)
   *
   * @param inverterNumber - Inverter number (1-7)
   * @param skidConfiguration - Skid configuration (e.g., "N3", "N5")
   * @returns true if inverter field should be disabled
   */
  isInverterFieldDisabled(
    inverterNumber: number,
    skidConfiguration: string | null | undefined
  ): boolean {
    const role = this._currentProjectRole;

    // Always disabled for Integrator and ReadOnly
    if (role === ProjectRole.INTEGRATOR || role === ProjectRole.READONLY) {
      return true;
    }

    // For other roles, enable based on skid configuration
    if (!skidConfiguration) {
      return true;
    }

    // Extract number from configuration (e.g., "N3" -> 3)
    const configNumber = parseInt(skidConfiguration.substring(1));

    // Enable if inverter number is within configuration range
    return inverterNumber > configNumber;
  }

  /**
   * Clear permission cache
   * Should be called when data changes that affects permissions
   */
  clearCache(): void {
    this.fieldPermissionCache.clear();
  }

  /**
   * Get current project role ID
   * Useful for template usage when needed
   */
  get currentProjectRole(): number {
    return this._currentProjectRole;
  }
}
